import { useState, type ReactNode } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { formatDateTime, formatRelative } from "@/lib/format";
import {
  acknowledgeErrorLog,
  fetchErrorLogDetail,
  requestCursorPrompt,
  requestErrorLogAiAnalysis,
  resetErrorLogSeverity,
  setErrorLogSeverity,
  type ErrorLogAiAnalysis,
  type ErrorLogEntry,
  type ErrorSeverity,
} from "@/lib/errorLogsApi";

const SEVERITY_META: Record<ErrorSeverity, { label: string; badgeClass: string }> = {
  kritisch: {
    label: "Kritisch",
    badgeClass: "bg-status-red/15 text-status-red border-status-red/30",
  },
  warnung: {
    label: "Warnung",
    badgeClass: "bg-status-orange/15 text-status-orange border-status-orange/30",
  },
  unkritisch: {
    label: "Unkritisch",
    badgeClass: "bg-fill-tertiary text-label-secondary border-separator",
  },
};

function renderMarkdownLite(text: string): ReactNode {
  const lines = text.split("\n");
  return lines.map((line, idx) => {
    const heading = line.match(/^### (.+)$/);
    if (heading) {
      return (
        <h4 key={idx} className="mt-3 text-sm font-semibold text-label">
          {heading[1]}
        </h4>
      );
    }
    const heading2 = line.match(/^## (.+)$/);
    if (heading2) {
      return (
        <h3 key={idx} className="mt-3 text-sm font-semibold text-label">
          {heading2[1]}
        </h3>
      );
    }
    if (!line.trim()) {
      return <div key={idx} className="h-2" />;
    }
    const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return (
      <p key={idx} className="text-sm text-label-secondary">
        {parts.map((part, partIdx) => {
          const bold = part.match(/^\*\*(.+)\*\*$/);
          if (bold) return <strong key={partIdx}>{bold[1]}</strong>;
          const code = part.match(/^`(.+)`$/);
          if (code) {
            return (
              <code key={partIdx} className="rounded bg-fill-tertiary px-1 py-0.5 text-xs">
                {code[1]}
              </code>
            );
          }
          return <span key={partIdx}>{part}</span>;
        })}
      </p>
    );
  });
}

interface ErrorLogItemProps {
  error: ErrorLogEntry;
  aiEnabled: boolean;
  /** Nach Acknowledge oder Einstufungs-Änderung: Liste neu laden. */
  onChanged: () => void;
}

export function ErrorLogItem({ error, aiEnabled, onChanged }: ErrorLogItemProps) {
  const [ackLoading, setAckLoading] = useState(false);
  const [severityLoading, setSeverityLoading] = useState(false);
  const [tracebackOpen, setTracebackOpen] = useState(false);
  const [traceback, setTraceback] = useState<string | null>(null);
  const [tracebackLoading, setTracebackLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<ErrorLogAiAnalysis | null>(
    error.latest_ai_analysis ?? null,
  );
  const [aiError, setAiError] = useState<string | null>(null);
  const [cursorPrompt, setCursorPrompt] = useState<string | null>(
    error.latest_cursor_prompt?.analysis ?? null,
  );
  const [cursorPromptLoading, setCursorPromptLoading] = useState(false);
  const [cursorPromptCopied, setCursorPromptCopied] = useState(false);

  const requestText =
    error.request_method || error.request_path
      ? `${error.request_method || "?"} ${error.request_path || "?"}${error.request_status ? ` → HTTP ${error.request_status}` : ""}`
      : "(kein HTTP-Kontext)";

  async function handleAck() {
    setAckLoading(true);
    try {
      await acknowledgeErrorLog(error.fingerprint);
      onChanged();
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "Markieren fehlgeschlagen.");
    } finally {
      setAckLoading(false);
    }
  }

  async function handleSeverityChange(value: string) {
    setSeverityLoading(true);
    setAiError(null);
    try {
      if (value === "auto") {
        await resetErrorLogSeverity(error.fingerprint);
      } else {
        await setErrorLogSeverity(error.fingerprint, value as ErrorSeverity);
      }
      onChanged();
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "Einstufung fehlgeschlagen.");
    } finally {
      setSeverityLoading(false);
    }
  }

  async function handleCursorPrompt() {
    setCursorPromptLoading(true);
    setAiError(null);
    try {
      const result = await requestCursorPrompt(error.fingerprint);
      setCursorPrompt(result.prompt);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "Cursor-Prompt fehlgeschlagen.");
    } finally {
      setCursorPromptLoading(false);
    }
  }

  async function handleCopyCursorPrompt() {
    if (!cursorPrompt) return;
    try {
      await navigator.clipboard.writeText(cursorPrompt);
      setCursorPromptCopied(true);
      window.setTimeout(() => setCursorPromptCopied(false), 2000);
    } catch {
      // Clipboard nicht verfügbar (z. B. unsicherer Kontext) – Text bleibt
      // im Feld markier- und kopierbar.
    }
  }

  async function toggleTraceback() {
    if (tracebackOpen) {
      setTracebackOpen(false);
      return;
    }
    setTracebackOpen(true);
    if (traceback !== null || error.sample_traceback) {
      setTraceback(error.sample_traceback ?? traceback);
      return;
    }
    setTracebackLoading(true);
    try {
      const detail = await fetchErrorLogDetail(error.fingerprint);
      setTraceback(detail.sample_traceback || "(Kein Traceback gespeichert)");
      if (detail.latest_ai_analysis) {
        setAiResult(detail.latest_ai_analysis);
      }
      if (detail.latest_cursor_prompt && !cursorPrompt) {
        setCursorPrompt(detail.latest_cursor_prompt.analysis);
      }
    } catch (err) {
      setTraceback(err instanceof Error ? err.message : "Traceback konnte nicht geladen werden.");
    } finally {
      setTracebackLoading(false);
    }
  }

  async function handleAiAnalysis() {
    if (!aiEnabled) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await requestErrorLogAiAnalysis(error.fingerprint);
      setAiResult({
        id: result.id,
        model: result.model,
        analysis: result.analysis,
        created_at: result.created_at,
        created_by: result.created_by,
      });
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "KI-Analyse fehlgeschlagen.");
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <article className="rounded-lg border border-separator bg-card p-4 shadow-ios">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span
            className={clsx(
              "rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
              SEVERITY_META[error.severity]?.badgeClass ?? SEVERITY_META.kritisch.badgeClass,
            )}
            title={
              error.severity_source === "manual"
                ? `Manuell eingestuft${error.severity_classified_by ? ` von ${error.severity_classified_by}` : ""}`
                : "Automatisch eingestuft"
            }
          >
            {SEVERITY_META[error.severity]?.label ?? error.severity}
            {error.severity_source === "auto" ? " (auto)" : ""}
          </span>
          <h3 className="font-mono text-sm font-semibold text-status-red">{error.exception_type}</h3>
        </div>
        <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary">
          {error.occurrences}× · zuletzt {formatRelative(error.last_seen)}
        </span>
      </div>

      <p className="mt-2 text-sm text-label">{error.exception_message}</p>

      <dl className="mt-3 grid gap-1 text-xs text-label-secondary sm:grid-cols-2">
        <div>
          <dt className="inline font-semibold text-label">Stelle: </dt>
          <dd className="inline">
            {error.user_code_file || "–"}
            {error.user_code_line ? `:${error.user_code_line}` : ""}
            {error.user_code_func ? ` in ${error.user_code_func}` : ""}
          </dd>
        </div>
        <div>
          <dt className="inline font-semibold text-label">Request: </dt>
          <dd className="inline">{requestText}</dd>
        </div>
        <div>
          <dt className="inline font-semibold text-label">Erstmals: </dt>
          <dd className="inline">{formatDateTime(error.first_seen)}</dd>
        </div>
        <div>
          <dt className="inline font-semibold text-label">Logdateien: </dt>
          <dd className="inline">{(error.log_files || []).join(", ") || "–"}</dd>
        </div>
      </dl>

      {error.user_code_chain.length > 0 ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-label-secondary">
            Eigener Call-Stack ({error.user_code_chain.length} Frames)
          </summary>
          <ul className="mt-2 space-y-1 border-l-2 border-separator pl-3 text-xs">
            {error.user_code_chain.map((frame, idx) => (
              <li key={`${frame.file}-${frame.line}-${idx}`} className="font-mono text-label-secondary">
                {frame.file}:{frame.line}{" "}
                <span className="text-label-tertiary">in</span>{" "}
                <code>{frame.func}</code>
                {idx === error.user_code_chain.length - 1 ? (
                  <span className="ml-2 rounded bg-status-red/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-status-red">
                    Ursprung
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="primary" disabled={ackLoading} onClick={() => void handleAck()}>
          {ackLoading ? "…" : "Erledigt markieren"}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={() => void toggleTraceback()}>
          {tracebackOpen ? "Traceback ausblenden" : "Traceback anzeigen"}
        </Button>
        {aiEnabled ? (
          <Button type="button" size="sm" variant="secondary" disabled={aiLoading} onClick={() => void handleAiAnalysis()}>
            {aiLoading ? "KI analysiert …" : "Mit KI analysieren"}
          </Button>
        ) : (
          <span className="self-center text-xs text-label-tertiary">KI deaktiviert</span>
        )}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={cursorPromptLoading}
          onClick={() => void handleCursorPrompt()}
        >
          {cursorPromptLoading ? "Prompt wird erstellt …" : "Cursor-Prompt erstellen"}
        </Button>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-label-secondary">
          Einstufung:
          <select
            value={error.severity_source === "manual" ? error.severity : "auto"}
            disabled={severityLoading}
            onChange={(event) => void handleSeverityChange(event.target.value)}
            className="rounded-md border border-separator bg-card px-2 py-1 text-xs text-label"
          >
            <option value="auto">Automatisch</option>
            <option value="kritisch">Kritisch</option>
            <option value="warnung">Warnung</option>
            <option value="unkritisch">Unkritisch</option>
          </select>
        </label>
      </div>

      {tracebackOpen ? (
        <div className="mt-3">
          {tracebackLoading ? (
            <div className="flex items-center gap-2 text-sm text-label-secondary">
              <Spinner className="h-3 w-3" />
              Traceback wird geladen …
            </div>
          ) : (
            <pre className="max-h-64 overflow-auto rounded-lg bg-fill-tertiary p-3 text-xs text-label-secondary">
              {traceback}
            </pre>
          )}
        </div>
      ) : null}

      {aiLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-label-secondary">
          <Spinner className="h-3 w-3" />
          KI analysiert den Fehler … (max. 45 s)
        </div>
      ) : null}

      {aiError ? (
        <Alert variant="error" className="mt-3">
          {aiError}
        </Alert>
      ) : null}

      {aiResult ? (
        <div className="mt-3 rounded-lg border border-separator bg-fill-tertiary/50 p-3">
          {renderMarkdownLite(aiResult.analysis)}
          <p className="mt-2 text-xs text-label-tertiary">
            Modell: <code>{aiResult.model}</code>
            {aiResult.created_at ? ` · ${formatDateTime(aiResult.created_at)}` : ""}
            {aiResult.created_by ? ` · ${aiResult.created_by}` : ""}
          </p>
        </div>
      ) : null}

      {cursorPrompt ? (
        <div className="mt-3 rounded-lg border border-separator bg-fill-tertiary/50 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-label">Cursor-Prompt</h4>
            <Button type="button" size="sm" variant="secondary" onClick={() => void handleCopyCursorPrompt()}>
              {cursorPromptCopied ? "Kopiert ✓" : "In Zwischenablage kopieren"}
            </Button>
          </div>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-card p-3 text-xs text-label-secondary">
            {cursorPrompt}
          </pre>
        </div>
      ) : null}
    </article>
  );
}
